import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Badeloft 2026-08-29: cart mutations run one at a time, in the order they were requested. */
  #updateChain = Promise.resolve();

  /** Number of cart mutations queued or in flight. */
  #pendingUpdates = 0;

  /**
   * Badeloft 2026-08-29: line keys the shopper has removed whose /cart/change has not been
   * answered yet. Each server response is rendered by the morph, and until a line's own
   * request has been applied the server HTML still contains it, so the morph would put the
   * row back for a moment. Rows for these keys are dropped again straight after every morph.
   * @type {Set<string>}
   */
  #pendingRemovalKeys = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Badeloft: address the line by its immutable key. The index is baked into the HTML at
    // render time and goes stale the moment any row above it is removed.
    const lineItemRow = this.#rowFor(event.target, line);
    const key = lineItemRow?.dataset.key;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    const cartItemRowToRemove = this.#rowFor(event?.target, line);
    const key = cartItemRowToRemove?.dataset.key;

    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove || cartItemRowToRemove.classList.contains('removing')) return;

    if (key) this.#pendingRemovalKeys.add(key);

    const liveRows = (this.refs.cartItemRows || []).filter((row) => !row.classList.contains('removing'));

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...liveRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state.
    // Badeloft: count only rows still live, so a row mid-removal can never make a
    // non-empty cart render as empty.
    const isEmptyCart = rowsToRemove.length >= liveRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const rowKey = row.dataset.key;
      // Only remove the node if it still represents the line we were asked to remove.
      const remove = () => {
        if (row.dataset.key === rowKey) row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    // Badeloft 2026-08-29: serialise mutations so responses are applied in request order.
    // Overlapping requests used to morph out of order and desync the rows from the real cart.
    const run = () => this.#performUpdate(config);
    this.#pendingUpdates += 1;
    this.setAttribute('data-bf-busy', '');
    const settle = () => {
      this.#pendingUpdates -= 1;
      if (this.#pendingUpdates <= 0) {
        this.#pendingUpdates = 0;
        this.removeAttribute('data-bf-busy');
        // Fired once every queued mutation has been applied; safe point to verify the view.
        this.dispatchEvent(new CustomEvent('bf:cart-settled', { bubbles: true }));
      }
    };
    this.#updateChain = this.#updateChain.then(run, run).then(settle, settle);
    return this.#updateChain;
  }

  /**
   * Performs one cart line update.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>}
   */
  #performUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(key ? { id: key } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, key, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // Badeloft 2026-08-29: mutations are serialised and every response carries the full cart,
        // so only the response to the LAST queued mutation is rendered. Rendering the intermediate
        // states would paint lines the shopper has already removed (and, after removing everything,
        // an empty table) on top of the optimistic view until the final response arrives.
        if (this.#pendingUpdates <= 1) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
          this.#afterMorph();

          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (key && quantity === 0) this.#pendingRemovalKeys.delete(key);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, key, parsedResponseText) => {
    const rows = this.refs.cartItemRows || [];
    const row = key ? this.querySelector(`[data-key="${key}"]`) : null;
    const currentLine = row instanceof HTMLElement && rows.indexOf(row) >= 0 ? rows.indexOf(row) + 1 : line;

    const quantitySelector = (this.refs.quantitySelectors || [])[currentLine - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const cartItemError = this.refs[`cartItemError-${currentLine}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${currentLine}`];

    if (
      !quantityInput ||
      !(cartItemError instanceof HTMLElement) ||
      !(cartItemErrorContainer instanceof HTMLElement)
    ) {
      // The rendered rows no longer match the request; re-render from the server rather than throw.
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    quantityInput.value = quantityInput.defaultValue;
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#afterMorph();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Re-applies client-side state the morph strips. The morph syncs this element's attributes from
   * the server HTML, which removes data-bf-busy while mutations are still queued; other scripts
   * (header-actions drawer refresh, bf-cart-update-guard) rely on that flag being accurate.
   */
  #afterMorph() {
    if (this.#pendingUpdates > 0) this.setAttribute('data-bf-busy', '');
    this.#dropPendingRemovalRows();
  }

  /**
   * Removes rows whose line the shopper has already removed but whose request is still queued.
   * Runs synchronously after each morph so the row never reaches a paint.
   */
  #dropPendingRemovalRows() {
    if (this.#pendingRemovalKeys.size === 0) return;
    for (const row of this.querySelectorAll('[ref="cartItemRows[]"]')) {
      const { key, parentKey } = row.dataset;
      if ((key && this.#pendingRemovalKeys.has(key)) || (parentKey && this.#pendingRemovalKeys.has(parentKey))) {
        row.remove();
      }
    }
  }

  /**
   * Resolves the cart row a control belongs to. Prefers the control's DOM ancestor, because the
   * line index baked into the markup goes stale as soon as a row above it is removed.
   * @param {EventTarget | null | undefined} target
   * @param {number} line
   * @returns {HTMLElement | undefined}
   */
  #rowFor(target, line) {
    if (target instanceof Element) {
      const row = target.closest('[ref="cartItemRows[]"]');
      if (row instanceof HTMLElement && this.contains(row)) return row;
    }
    return (this.refs.cartItemRows || [])[line - 1];
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
